import React from 'react';
import { Link } from 'react-router-dom';
import type { CardAction, CardActionResult } from '../../../utils/cardActionHandler';

export type OnAction = (action: CardAction) => Promise<CardActionResult | undefined> | void;

export interface F8CardData {
  type: string;
  data?: any;
  actions?: CardAction[];
}

/** Initials from a display name, max 2 characters. */
export function initials(name: string | undefined | null): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/** Hash a string to a stable hue 0-360 (matches design/pplkpr/modern.jsx pattern). */
export function hashHue(s: string | undefined | null): number {
  if (!s) return 280;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

/** Avatar with photo fallback to initials. */
export function CardAvatar({
  src,
  name,
  size = 40,
  className,
}: {
  src?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={`f8c-avatar ${className || ''}`.trim()}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {src ? <img src={src} alt={name || ''} /> : initials(name)}
    </div>
  );
}

/**
 * InlineShell — v2 inline card frame. No bordered container; a 1.5px dashed
 * magenta left rail with an 8px gradient anchor dot at the top, so the card
 * reads as a continuation of the agent's reply rather than a popped-out widget.
 */
export function InlineShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`f8c-inline-shell ${className || ''}`.trim()}>
      <span className="f8c-inline-anchor" />
      {children}
    </div>
  );
}

/**
 * InlineEyebrow — tiny uppercase tracked label sitting above card content,
 * with optional muted trailing meta (e.g. "for Alex").
 */
export function InlineEyebrow({
  label,
  trailing,
}: {
  label: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="f8c-inline-eyebrow">
      <span>{label}</span>
      {trailing && <span className="f8c-inline-eyebrow-trailing">{trailing}</span>}
    </div>
  );
}

/**
 * CardButton — pill button matching design/pplkpr/cards.jsx CardButton.
 * Primary: gradient + shadow. Secondary: glass + border.
 */
export function CardButton({
  children,
  primary,
  flex,
  small,
  onClick,
  disabled,
  style,
}: {
  children: React.ReactNode;
  primary?: boolean;
  flex?: boolean;
  small?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: flex ? 1 : undefined,
        appearance: 'none',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: 999,
        padding: small ? '7px 12px' : '10px 16px',
        fontSize: small ? 12 : 13.5,
        fontWeight: 600,
        color: '#fff',
        fontFamily: 'inherit',
        background: primary
          ? 'linear-gradient(135deg, #d946ef 0%, #a855f7 100%)'
          : 'rgba(255,255,255,0.06)',
        border: primary ? 0 : '1px solid rgba(255,255,255,0.12)',
        boxShadow: primary ? '0 4px 14px rgba(217,70,239,0.35)' : 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
        transition: 'filter 0.15s ease, transform 0.1s ease',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** Format a timestamp as "now / 5m / 3h / 2d / 1w". */
export function formatRelativeShort(input: any): string {
  if (!input) return '';
  let date: Date;
  if (typeof input === 'string') date = new Date(input);
  else if (typeof input === 'number') date = new Date(input);
  else if (input?.toDate) date = input.toDate();
  else if (input?._seconds) date = new Date(input._seconds * 1000);
  else date = new Date(input);
  if (isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return `${Math.floor(diffDay / 7)}w`;
}

/** Format an event datetime as "fri, mar 5 at 7:30 pm". */
export function formatEventDateTime(input: any): string {
  if (!input) return '';
  let date: Date;
  if (typeof input === 'string') date = new Date(input);
  else if (typeof input === 'number') date = new Date(input);
  else if (input?.toDate) date = input.toDate();
  else if (input?._seconds) date = new Date(input._seconds * 1000);
  else date = new Date(input);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const isThisYear = date.getFullYear() === now.getFullYear();
  const dateOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(isThisYear ? {} : { year: 'numeric' }),
  };
  const datePart = date.toLocaleDateString('en-US', dateOpts).toLowerCase();
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  return `${datePart} at ${timePart}`;
}

// ─── CardErrorNotice — why an in-card action failed, and what to do ─────────

/**
 * A refusal the user can act on, said inside the card that caused it (PPL-508).
 * In-card joins used to fail silently on web — the button snapped back and the
 * reason never reached the screen. `text` is always the server's own sentence;
 * the link only appears when the failure is the first-name gate, i.e. when
 * there is somewhere to send the user.
 */
export function CardErrorNotice({
  text,
  actionLabel,
  actionTo,
}: {
  text?: string | null;
  actionLabel?: string | null;
  actionTo?: string | null;
}) {
  if (!text) return null;
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 12.5, lineHeight: '17px', color: 'rgba(220,200,240,0.8)' }}>
        {text}
      </span>
      {actionLabel && actionTo ? (
        <Link to={actionTo} style={{ fontSize: 12.5, fontWeight: 600, color: '#f0abfc' }}>
          {actionLabel} →
        </Link>
      ) : null}
    </div>
  );
}
